import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSecurityDeposits, deleteSecurityDeposit, SecurityDeposit } from './SecurityDepositApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faEye,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString() : '-');

const SecurityDepositList: React.FC = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();

  const [deposits, setDeposits] = useState<SecurityDeposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDeposits = async () => {
    try {
      const response = await getSecurityDeposits(contractId!);
      setDeposits(response.data);
    } catch (err) {
      setError('Failed to load security deposits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeposits();
  }, [contractId]);

  const handleDelete = async (depositId: string) => {
    if (confirm('Are you sure you want to delete this deposit?')) {
      await deleteSecurityDeposit(depositId, contractId!);
      loadDeposits();
    }
  };

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/contracts/${contractId}`)}
        className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-800 transition"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Back to Contract
      </button>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Security Deposits</h2>
        <button
          onClick={() => navigate(`/contracts/${contractId}/security-deposit/create`)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2 transition"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Security Deposit
        </button>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border border-gray-300 text-sm rounded">
            <thead className="bg-gray-200">
              <tr>
                {['depositType', 'instrumentNo', 'amount', 'bankName', 'depositCreatedDate', 'depositSubmitedDate', 'status'].map((field) => (
                  <th key={field} className="border px-4 py-2 text-left font-semibold capitalize">
                    {field.replace(/([A-Z])/g, ' $1')}
                  </th>
                ))}
                <th className="border px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map((deposit) => (
                <tr key={deposit.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{deposit.depositType}</td>
                  <td className="border px-4 py-2">{deposit.instrumentNo}</td>
                  <td className="border px-4 py-2">₹{deposit.amount.toLocaleString()}</td>
                  <td className="border px-4 py-2">{deposit.bankName}</td>
                  <td className="border px-4 py-2">{formatDate(deposit.depositCreatedDate)}</td>
                  <td className="border px-4 py-2">{formatDate(deposit.depositSubmitedDate)}</td>
                  <td className="border px-4 py-2">{deposit.status}</td>
                  <td className="border px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 transition"
                        onClick={() => navigate(`/contracts/${contractId}/security-deposit/${deposit.id}`)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View
                      </button>
                      <button
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded flex items-center gap-1 transition"
                        onClick={() => navigate(`/contracts/${contractId}/security-deposit/${deposit.id}/edit`)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Edit
                      </button>
                      <button
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded flex items-center gap-1 transition"
                        onClick={() => deposit.id && handleDelete(deposit.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SecurityDepositList;